/**
 * Annotated diagram of a disposable smart label — thin, adhesive-backed,
 * with callouts for antenna, MCU + connectivity module, battery, and
 * activation pull-tab. Pure SVG so it scales and inherits brand colours.
 */
import React from "react";

export default function AnnotatedLabel({ title = "Disposable smart label — anatomy" }) {
  return (
    <figure
      className="my-10 border border-line bg-white p-8 lg:p-10"
      data-testid="annotated-label-diagram"
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-widest text-ink-subtle mb-6">
        {title}
      </figcaption>
      <svg
        viewBox="0 0 640 360"
        role="img"
        aria-label="Annotated diagram of a disposable smart label showing antenna, MCU, connectivity module, battery and pull-tab activation"
        className="w-full h-auto"
      >
        {/* Label body */}
        <defs>
          <linearGradient id="lbl-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#F7F7F5" />
            <stop offset="1" stopColor="#EFEFEC" />
          </linearGradient>
        </defs>

        {/* soft drop-shadow of label */}
        <rect x="182" y="122" width="276" height="132" rx="10" fill="#0A0A0A" opacity="0.06" />
        {/* label */}
        <rect x="180" y="120" width="276" height="128" rx="10" fill="url(#lbl-body)" stroke="#D6D6D2" />

        {/* Antenna trace */}
        <path
          d="M 210 140 L 425 140 L 425 168 L 240 168 L 240 200 L 425 200 L 425 224 L 210 224 Z"
          fill="none"
          stroke="#0047AB"
          strokeWidth="2"
          opacity="0.7"
        />

        {/* MCU module */}
        <rect x="256" y="170" width="60" height="30" rx="3" fill="#0A0A0A" />
        <text x="286" y="190" textAnchor="middle" fontSize="10" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">
          MCU
        </text>

        {/* Battery */}
        <rect x="330" y="170" width="80" height="30" rx="3" fill="#fff" stroke="#0A0A0A" strokeWidth="1.5" />
        <rect x="405" y="177" width="6" height="16" fill="#0A0A0A" />
        <text x="370" y="190" textAnchor="middle" fontSize="10" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">
          BATTERY
        </text>

        {/* Pull-tab activation */}
        <rect x="418" y="164" width="42" height="42" rx="2" fill="#0047AB" opacity="0.15" stroke="#0047AB" strokeDasharray="3 3" />
        <text x="439" y="188" textAnchor="middle" fontSize="9" fill="#0047AB" fontFamily="ui-monospace, SFMono-Regular, monospace">
          PULL
        </text>

        {/* Callout — Antenna */}
        <line x1="220" y1="140" x2="120" y2="70" stroke="#0A0A0A" strokeWidth="0.75" />
        <circle cx="220" cy="140" r="3" fill="#0047AB" />
        <text x="20" y="60" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">01 · BLE / cellular antenna</text>
        <text x="20" y="76" fontSize="10" fill="#525252">Copper trace embedded in the label substrate.</text>

        {/* Callout — MCU */}
        <line x1="286" y1="200" x2="286" y2="308" stroke="#0A0A0A" strokeWidth="0.75" />
        <circle cx="286" cy="200" r="3" fill="#0047AB" />
        <text x="230" y="326" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">02 · MCU + radio</text>
        <text x="230" y="342" fontSize="10" fill="#525252">Location, sensor sampling, and reporting logic.</text>

        {/* Callout — Battery */}
        <line x1="370" y1="200" x2="500" y2="308" stroke="#0A0A0A" strokeWidth="0.75" />
        <circle cx="370" cy="200" r="3" fill="#0047AB" />
        <text x="460" y="326" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">03 · Non-replaceable battery</text>
        <text x="460" y="342" fontSize="10" fill="#525252">Sized for one shipment lifecycle.</text>

        {/* Callout — Pull-tab */}
        <line x1="440" y1="164" x2="560" y2="70" stroke="#0A0A0A" strokeWidth="0.75" />
        <circle cx="440" cy="164" r="3" fill="#0047AB" />
        <text x="480" y="60" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">04 · Pull-tab activation</text>
        <text x="480" y="76" fontSize="10" fill="#525252">Peel/cut to activate at dispatch.</text>

        {/* Adhesive back stripe */}
        <text x="182" y="272" fontSize="9" fill="#B7B7B0" fontFamily="ui-monospace, SFMono-Regular, monospace">
          — adhesive-backed substrate · apply directly to parcel, carton or pallet —
        </text>
      </svg>
    </figure>
  );
}
